import {
  AlertCircle,
  BrainCircuit,
  Database,
  Eye,
  FileText,
  Languages,
  Plug,
  Shield,
  Sliders,
  Sparkles,
  User,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/ui/section-card";
import type { AgentDraft } from "@/features/agents/components/AgentDraft";
import { PromptPreviewDialog } from "@/features/agents/components/PromptPreviewDialog";
import { useKbs } from "@/features/knowledge-bases/hooks/useKbs";
import { useLlmModels } from "@/features/llms/hooks/useLlmModels";
import { useMcps } from "@/features/mcps/hooks/useMcps";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { useUsers } from "@/features/users/hooks/useUsers";
import { cn } from "@/lib/utils";

interface PrePublishSummaryProps {
  draft: AgentDraft;
  agentId?: string;
}

/**
 * Resumo consolidado do agente exibido inline na aba Publicacao. O
 * objetivo e que o usuario veja num bloco o que vai ser publicado
 * (modelo, KBs, MCPs, visibilidade, behaviors) sem precisar voltar
 * pelas 7 abas. Pinta validation hints em amber para campos
 * obrigatorios vazios ou inconsistencias suaves (nao bloqueia
 * publicacao -- backend e fonte da verdade).
 *
 * Agente EXTERNO: modelo/comportamento/conhecimento/ferramentas/prompt
 * nao se aplicam (quem responde e o bot remoto) -- o resumo mostra
 * Identidade + Conexao externa + Visibilidade.
 */
export function PrePublishSummary({
  draft,
  agentId,
}: PrePublishSummaryProps) {
  const { t } = useTranslation("agents");
  const [promptOpen, setPromptOpen] = useState(false);
  const isExternal = draft.agent_type === "external";

  const llmModels = useLlmModels({ include_inactive: true });
  const kbs = useKbs({ include_inactive: true });
  const mcps = useMcps({ include_inactive: true });
  const teams = useTeams({ include_inactive: true });
  const users = useUsers({ is_active: true });

  const llm = useMemo(
    () => llmModels.data?.find((m) => m.id === draft.llm_model_id) ?? null,
    [llmModels.data, draft.llm_model_id],
  );
  const team = useMemo(
    () => teams.data?.find((t_) => t_.id === draft.owner_team_id) ?? null,
    [teams.data, draft.owner_team_id],
  );
  const kbChips = useMemo(() => {
    const map = new Map((kbs.data ?? []).map((kb) => [kb.id, kb.name]));
    return draft.kb_ids.map((id) => ({ id, name: map.get(id) ?? id }));
  }, [kbs.data, draft.kb_ids]);
  const mcpChips = useMemo(() => {
    const map = new Map((mcps.data ?? []).map((m) => [m.id, m.name]));
    return draft.mcp_items.map((it) => ({
      id: it.mcp_server_id,
      name: map.get(it.mcp_server_id) ?? it.mcp_server_id,
      tools: it.enabled_tools?.length ?? 0,
    }));
  }, [mcps.data, draft.mcp_items]);
  const visibilityTeamNames = useMemo(() => {
    const map = new Map((teams.data ?? []).map((t_) => [t_.id, t_.name]));
    return draft.visibility_team_ids.map((id) => map.get(id) ?? id);
  }, [teams.data, draft.visibility_team_ids]);
  const visibilityUserNames = useMemo(() => {
    const map = new Map(
      (users.data ?? []).map((u) => [u.id, u.name || u.email]),
    );
    return draft.visibility_user_ids.map((id) => map.get(id) ?? id);
  }, [users.data, draft.visibility_user_ids]);

  const showCiteWithoutKbWarn =
    !isExternal && draft.cite_sources && draft.kb_ids.length === 0;

  return (
    <>
      <SectionCard
        title={
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-brand-500" />
            {t("publication.summary.title")}
          </span>
        }
        description={t("publication.summary.description")}
      >
        <div className="grid gap-3 lg:grid-cols-2">
          <SummaryBlock
            icon={User}
            title={t("publication.summary.identity")}
          >
            <KV
              k={t("publication.summary.fields.name")}
              v={draft.name || <Incomplete t={t} />}
            />
            {/* Slug vazio NAO e incompleto: o backend gera a partir do
                nome no create. So mostra o aviso se nem nome ha. */}
            <KV
              k={t("publication.summary.fields.slug")}
              v={
                draft.slug || (
                  <span className="text-ink-500">
                    {t("publication.summary.fields.slugAuto")}
                  </span>
                )
              }
            />
            {draft.description ? (
              <KV
                k={t("publication.summary.fields.description")}
                v={
                  <span className="line-clamp-2 break-words">
                    {draft.description}
                  </span>
                }
              />
            ) : null}
            <KV
              k={t("publication.summary.fields.ownerTeam")}
              v={team ? team.name : <Incomplete t={t} />}
            />
            {isExternal ? (
              <KV
                k={t("external.typeSelectorLabel")}
                v={<Badge variant="outline">{t("external.badge")}</Badge>}
              />
            ) : null}
          </SummaryBlock>

          {isExternal ? (
            <SummaryBlock
              icon={Plug}
              title={t("publication.summary.externalConnection")}
            >
              <KV
                k={t("external.baseUrlLabel")}
                v={
                  draft.external_config?.base_url ? (
                    <span className="break-all font-mono">
                      {draft.external_config.base_url}
                    </span>
                  ) : (
                    <Incomplete t={t} />
                  )
                }
              />
              <KV
                k={t("external.authTypeLabel")}
                v={t(
                  `external.authTypes.${draft.external_config?.auth_type ?? "none"}`,
                )}
              />
              <KV
                k={t("external.langfuseSection")}
                v={
                  <Badge
                    variant={
                      draft.external_config?.langfuse_host &&
                      draft.external_config?.langfuse_public_key &&
                      draft.external_config?.has_langfuse_secret
                        ? "default"
                        : "secondary"
                    }
                  >
                    {draft.external_config?.langfuse_host &&
                    draft.external_config?.langfuse_public_key &&
                    draft.external_config?.has_langfuse_secret
                      ? t("publication.summary.on")
                      : t("publication.summary.off")}
                  </Badge>
                }
              />
              {draft.external_config?.health_status ? (
                <KV
                  k={t("publication.summary.externalHealth")}
                  v={
                    <Badge
                      variant={
                        draft.external_config.health_status === "ok"
                          ? "default"
                          : "destructive"
                      }
                    >
                      {t(
                        `external.health.${draft.external_config.health_status}`,
                      )}
                    </Badge>
                  }
                />
              ) : null}
              {!draft.external_config?.base_url ? (
                <Warning t={t}>
                  {t("publication.summary.warnings.externalNotConfigured")}
                </Warning>
              ) : null}
            </SummaryBlock>
          ) : null}

          {!isExternal ? (
          <>
          <SummaryBlock
            icon={BrainCircuit}
            title={t("publication.summary.model")}
          >
            <KV
              k={t("publication.summary.fields.llm")}
              v={
                llm ? (
                  <span className="inline-flex items-center gap-2">
                    <span>{llm.display_name}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {llm.provider}
                    </Badge>
                  </span>
                ) : (
                  <Incomplete t={t} />
                )
              }
            />
            {llm?.supports_reasoning ? (
              <KV
                k={t("publication.summary.fields.reasoning")}
                v={
                  draft.reasoning_effort ?? (
                    <span className="text-ink-500">
                      {t("publication.summary.fields.default")}
                    </span>
                  )
                }
              />
            ) : null}
            <KV
              k={t("publication.summary.fields.piiFilter")}
              v={
                <Badge variant={draft.pii_filter_enabled ? "default" : "secondary"}>
                  {draft.pii_filter_enabled
                    ? t("publication.summary.on")
                    : t("publication.summary.off")}
                </Badge>
              }
            />
          </SummaryBlock>

          <SummaryBlock
            icon={Sliders}
            title={t("publication.summary.behavior")}
          >
            <div className="flex flex-wrap gap-1.5">
              {draft.response_tone ? (
                <Badge variant="outline">
                  {t("publication.summary.fields.tone")}: {draft.response_tone}
                </Badge>
              ) : null}
              <Badge variant="outline" className="inline-flex items-center gap-1">
                <Languages className="h-3 w-3" /> {draft.response_language}
              </Badge>
              {draft.fallback_behavior ? (
                <Badge variant="outline">
                  {t("publication.summary.fields.fallback")}:{" "}
                  {draft.fallback_behavior}
                </Badge>
              ) : null}
              <Badge
                variant={draft.memory_enabled ? "default" : "secondary"}
              >
                {t("publication.summary.fields.memory")}
              </Badge>
              <Badge variant={draft.cite_sources ? "default" : "secondary"}>
                {t("publication.summary.fields.cite")}
              </Badge>
              <Badge
                variant={
                  draft.auto_escalate_low_confidence ? "default" : "secondary"
                }
              >
                {t("publication.summary.fields.escalate")}
              </Badge>
            </div>
            {showCiteWithoutKbWarn ? (
              <Warning t={t}>
                {t("publication.summary.warnings.citeWithoutKb")}
              </Warning>
            ) : null}
          </SummaryBlock>

          <SummaryBlock
            icon={Database}
            title={t("publication.summary.knowledge")}
          >
            {kbChips.length === 0 ? (
              <span className="text-xs text-ink-500">
                {t("publication.summary.empty.kb")}
              </span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {kbChips.map((c) => (
                  <Badge key={c.id} variant="outline" className="gap-1">
                    <Database className="h-3 w-3" />
                    {c.name}
                  </Badge>
                ))}
              </div>
            )}
          </SummaryBlock>

          <SummaryBlock
            icon={Plug}
            title={t("publication.summary.tools")}
          >
            <div className="flex flex-wrap gap-1.5">
              {draft.web_search_enabled ? (
                <Badge variant="default" className="gap-1">
                  {t("publication.summary.fields.webSearch")}
                </Badge>
              ) : null}
              {draft.reports_enabled ? (
                <Badge variant="default" className="gap-1">
                  {t("publication.summary.fields.reports")}
                </Badge>
              ) : null}
              {mcpChips.length === 0 &&
              !draft.web_search_enabled &&
              !draft.reports_enabled ? (
                <span className="text-xs text-ink-500">
                  {t("publication.summary.empty.mcp")}
                </span>
              ) : null}
              {mcpChips.map((c) => (
                <Badge key={c.id} variant="outline" className="gap-1">
                  <Plug className="h-3 w-3" />
                  {c.name}
                  <span className="text-[10px] text-ink-500">
                    ({c.tools})
                  </span>
                </Badge>
              ))}
            </div>
          </SummaryBlock>
          </>
          ) : null}

          <SummaryBlock
            icon={Eye}
            title={t("publication.summary.visibility")}
          >
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="outline" className="inline-flex items-center gap-1">
                <Shield className="h-3 w-3" />
                {t(`publication.summary.visibilityMode.${draft.visibility}`)}
              </Badge>
              {draft.visibility === "team_restricted" &&
              visibilityTeamNames.length > 0
                ? visibilityTeamNames.map((n) => (
                    <Badge
                      key={`vt-${n}`}
                      variant="outline"
                      className="gap-1"
                    >
                      <Users className="h-3 w-3" />
                      {n}
                    </Badge>
                  ))
                : null}
              {draft.visibility === "team_restricted" &&
              visibilityUserNames.length > 0
                ? visibilityUserNames.map((n) => (
                    <Badge
                      key={`vu-${n}`}
                      variant="outline"
                      className="gap-1"
                    >
                      <User className="h-3 w-3" />
                      {n}
                    </Badge>
                  ))
                : null}
            </div>
          </SummaryBlock>
        </div>

        {!isExternal ? (
        <div className="border-t border-line pt-4">
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
              <Sparkles className="h-3.5 w-3.5 text-brand-500" />
              {t("publication.summary.systemPrompt")}
            </h4>
            {agentId ? (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => setPromptOpen(true)}
                disabled={!draft.system_prompt}
              >
                {t("publication.summary.viewFull")}
              </Button>
            ) : null}
          </div>
          {draft.system_prompt ? (
            <pre className="mt-2 max-h-32 overflow-hidden whitespace-pre-wrap rounded-lg border border-line bg-canvas px-3 py-2 text-xs text-ink-900">
              {draft.system_prompt.split("\n").slice(0, 6).join("\n")}
              {draft.system_prompt.split("\n").length > 6 ? "…" : ""}
            </pre>
          ) : (
            <p className="mt-2 text-xs text-ink-500">
              {t("publication.summary.empty.prompt")}
            </p>
          )}
        </div>
        ) : null}
      </SectionCard>

      {agentId && !isExternal ? (
        <PromptPreviewDialog
          open={promptOpen}
          onOpenChange={setPromptOpen}
          agentId={agentId}
          draft={draft}
        />
      ) : null}
    </>
  );
}

function SummaryBlock({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof FileText;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-line bg-canvas p-3">
      <h5 className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
        <Icon className="h-3.5 w-3.5 text-brand-500" />
        {title}
      </h5>
      <div className="flex flex-col gap-1.5 text-xs text-ink-900">
        {children}
      </div>
    </div>
  );
}

function KV({ k, v }: { k: React.ReactNode; v: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="text-[10px] uppercase tracking-wide text-ink-500">
        {k}
      </span>
      <span className="min-w-0 text-xs text-ink-900">{v}</span>
    </div>
  );
}

function Incomplete({ t }: { t: (k: string) => string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        "gap-1 border-warning-500/40 bg-warning-500/10 text-warning-700",
      )}
    >
      <AlertCircle className="h-3 w-3" />
      {t("publication.summary.incomplete")}
    </Badge>
  );
}

function Warning({
  children,
  t: _t,
}: {
  children: React.ReactNode;
  t: (k: string) => string;
}) {
  return (
    <p className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-warning-700">
      <AlertCircle className="h-3 w-3 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
